/**
 * Notification Service — OMN-25
 *
 * Central infrastructure for sending notifications (email, in-app).
 * Email is delivered via the `send-notification-email` Edge Function, which
 * calls the Brevo REST API using the BREVO_API_KEY secret. If the secret is
 * not set on the project the function returns success=false and nothing is
 * sent — callers are non-blocking, so the primary action still succeeds.
 * To activate delivery: `supabase secrets set BREVO_API_KEY=<key> --project-ref <ref>`.
 *
 * Usage:
 *   import { NotificationService } from './notificationService';
 *   await NotificationService.send({ ... });
 */

import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { renderNotificationEmail, NotificationEmailContent } from './emailTemplates';

const APP_BASE_URL = (process.env.REACT_APP_HOSTING_URL || 'https://app.omniflyer.com').replace(/\/$/, '');
export function appUrl(path: string): string {
  return `${APP_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
}

// ─── Types ───────────────────────────────────────────────────

export type NotificationChannel = 'email' | 'in_app';

export type NotificationType =
  | 'squawk_reported'        // OMN-18
  | 'aircraft_grounded'      // OMN-19
  | 'booking_cancelled'      // OMN-20
  | 'booking_series_cancelled' // OMN-287 (single summary for a series cancel)
  | 'booking_created'
  | 'booking_completed'
  | 'maintenance_status_changed'
  | 'member_invited'
  | 'member_role_changed'
  | 'membership_request_approved'
  | 'membership_request_rejected';

export interface NotificationPayload {
  type: NotificationType;
  /** Organization ID */
  orgId: string;
  /** Target user IDs. Use ['*admin'] to notify all org admins. */
  recipientIds: string[];
  /** Subject line for email, title for in-app */
  subject: string;
  /** Plain-text body */
  body: string;
  /** Optional HTML body (for email) */
  htmlBody?: string;
  /** Structured content rendered into a branded HTML email (OMN-296). When set,
   *  send() renders the template (filling in org name) instead of using body/htmlBody. */
  emailContent?: Omit<NotificationEmailContent, 'orgName'>;
  /** Related entity for deep-linking */
  relatedEntity?: {
    type: 'booking' | 'aircraft' | 'maintenance_report' | 'member';
    id: string;
  };
  /** Extra metadata */
  metadata?: Record<string, unknown>;
}

/** Row shape of the org-scoped `notifications` table (the in-app surface read
 *  by NotificationContext). One row per event; visible to all org members;
 *  read_by tracks the user IDs who have dismissed it. */
export interface NotificationRecord {
  id: string;
  org_id: string;
  type: NotificationType | 'maintenance_due' | 'maintenance_overdue';
  aircraft_id: string | null;
  aircraft_name: string | null;
  message: string;
  severity: 'info' | 'warning' | 'error';
  read_by: string[];
  created_at: string;
}

// ─── Transport ───────────────────────────────────────────────

/**
 * Send a notification email via the `send-notification-email` Edge Function.
 * The Edge Function calls the Brevo REST API using the BREVO_API_KEY secret.
 *
 * If the secret is not set on the project the function returns success=false
 * and we log a warning — callers are non-blocking so this never throws.
 *
 * To activate email delivery, set the secret on both projects:
 *   supabase secrets set BREVO_API_KEY=<key> --project-ref <ref>
 */
async function sendEmail(
  recipientEmail: string,
  subject: string,
  body: string,
  htmlBody?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('send-notification-email', {
      body: { to: recipientEmail, subject, body, htmlBody },
    });

    if (error) {
      logger.error('[NotificationService] Edge Function invocation error', {
        to: recipientEmail,
        subject,
        error: error.message,
      });
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; error?: string };

    if (!result.success) {
      logger.warn('[NotificationService] Email not delivered', {
        to: recipientEmail,
        subject,
        reason: result.error,
      });
    } else {
      logger.info('[NotificationService] Email sent', { to: recipientEmail, subject });
    }

    return result;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logger.error('[NotificationService] sendEmail failed', { to: recipientEmail, subject, error: message });
    return { success: false, error: message };
  }
}

// ─── Service ─────────────────────────────────────────────────

export class NotificationService {
  /**
   * Send a notification to one or more recipients.
   * Resolves '*admin' to all admin users in the org.
   * Non-blocking — errors are logged but don't throw.
   */
  static async send(payload: NotificationPayload): Promise<void> {
    try {
      // Persist an org-scoped in-app notification for operationally-relevant
      // events so the dashboard surfaces them even if email delivery fails.
      // Independent of email recipient resolution: the notifications table is
      // org-scoped (visible to all org members; read_by tracks who dismissed it).
      await NotificationService.persistInAppNotification(payload);

      // Resolve recipient IDs (for email)
      let recipientIds = [...payload.recipientIds];

      // Expand '*admin' to all admins in the org
      if (recipientIds.includes('*admin')) {
        recipientIds = recipientIds.filter(id => id !== '*admin');
        const { data: admins } = await supabase
          .from('organization_members')
          .select('user_id')
          .eq('org_id', payload.orgId)
          .eq('role', 'admin');

        if (admins) {
          recipientIds.push(...admins.map(a => a.user_id));
        }
      }

      // Expand '*maintenance' to all maintenance users in the org
      if (recipientIds.includes('*maintenance')) {
        recipientIds = recipientIds.filter(id => id !== '*maintenance');
        const { data: maintUsers } = await supabase
          .from('organization_members')
          .select('user_id')
          .eq('org_id', payload.orgId)
          .eq('role', 'maintenance');

        if (maintUsers) {
          recipientIds.push(...maintUsers.map(m => m.user_id));
        }
      }

      // De-duplicate
      recipientIds = Array.from(new Set(recipientIds));

      if (recipientIds.length === 0) {
        logger.warn('[NotificationService] No recipients resolved', { type: payload.type, orgId: payload.orgId });
        return;
      }

      // Fetch recipient emails
      const { data: users } = await supabase
        .from('users')
        .select('id, email')
        .in('id', recipientIds);

      if (!users || users.length === 0) {
        logger.warn('[NotificationService] No user emails found', { recipientIds });
        return;
      }

      // Render the branded email template once (org-level) when structured
      // content is provided. Falls back to the caller's body/htmlBody otherwise.
      let emailBody = payload.body;
      let emailHtml = payload.htmlBody;
      if (payload.emailContent) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', payload.orgId)
          .maybeSingle();
        const rendered = renderNotificationEmail({ ...payload.emailContent, orgName: org?.name });
        emailHtml = rendered.html;
        emailBody = rendered.text;
      }

      // Send the email to each resolved recipient. Per-recipient delivery
      // status is logged inside sendEmail(); the in-app record is the org-scoped
      // row persisted above (not one row per recipient).
      for (const user of users) {
        await sendEmail(user.email, payload.subject, emailBody, emailHtml);
      }

      logger.info('[NotificationService] Notifications dispatched', {
        type: payload.type,
        recipientCount: users.length,
      });
    } catch (error) {
      // Non-blocking: log and swallow
      logger.error('[NotificationService] Failed to send notification', {
        type: payload.type,
        error: error instanceof Error ? error.message : error,
      });
    }
  }

  /** Operationally-relevant, org-wide event types that warrant an in-app
   *  notification, mapped to their default severity. Types not listed here
   *  (the personal booking and member events) stay email-only, because the
   *  org-scoped notifications table has no per-recipient targeting. */
  private static readonly IN_APP_SEVERITY: Partial<Record<NotificationType, 'info' | 'warning' | 'error'>> = {
    aircraft_grounded: 'error',
    squawk_reported: 'warning',
    maintenance_status_changed: 'info',
    member_role_changed: 'info',
  };

  /** In-app types that target a single user (recipient_id set) rather than
   *  broadcasting org-wide. */
  private static readonly PERSONAL_IN_APP_TYPES = new Set<NotificationType>(['member_role_changed']);

  /**
   * Insert one row into the `notifications` table so the event surfaces in the
   * dashboard NotificationContext. Org-wide events (grounding/squawk) leave
   * recipient_id NULL; personal events (member_role_changed) target the single
   * concrete recipient. Uses the table's real columns; read_by + created_at
   * default at the DB. Best-effort — failure is logged, never thrown.
   */
  private static async persistInAppNotification(payload: NotificationPayload): Promise<void> {
    const baseSeverity = NotificationService.IN_APP_SEVERITY[payload.type];
    if (!baseSeverity) return; // email-only type — not an in-app event

    // Personal types target one concrete recipient; org-wide types broadcast.
    let recipientId: string | null = null;
    if (NotificationService.PERSONAL_IN_APP_TYPES.has(payload.type)) {
      recipientId = payload.recipientIds.find(id => id && !id.startsWith('*')) ?? null;
      if (!recipientId) return; // no concrete recipient — nothing to surface in-app
    }

    const grounded = payload.metadata?.priority === 'grounded';
    const severity = payload.type === 'squawk_reported' && grounded ? 'error' : baseSeverity;
    const aircraftId = payload.relatedEntity?.type === 'aircraft' ? payload.relatedEntity.id : null;
    const aircraftName = (payload.metadata?.aircraftRegistration as string | undefined) ?? null;

    const { error } = await supabase.from('notifications').insert({
      org_id: payload.orgId,
      type: payload.type,
      recipient_id: recipientId,
      aircraft_id: aircraftId,
      aircraft_name: aircraftName,
      message: payload.subject,
      severity,
    });

    if (error) {
      logger.warn('[NotificationService] Could not persist in-app notification', {
        type: payload.type,
        orgId: payload.orgId,
        error: error.message,
      });
    }
  }

  // ─── Convenience methods for common notification types ──────

  /** OMN-18: Notify admins + maintenance when a squawk is reported */
  static async notifySquawkReported(
    orgId: string,
    reportId: string,
    aircraftRegistration: string,
    description: string,
    priority: string,
    reporterName: string
  ): Promise<void> {
    await this.send({
      type: 'squawk_reported',
      orgId,
      recipientIds: ['*admin', '*maintenance'],
      subject: `Squawk Reported: ${aircraftRegistration} (${priority})`,
      body: `A maintenance issue has been reported for ${aircraftRegistration}.\n\nPriority: ${priority}\nReported by: ${reporterName}\n\nDescription:\n${description}`,
      emailContent: {
        type: 'squawk_reported',
        badgeLabel: 'Squawk Reported',
        headline: `Squawk reported on ${aircraftRegistration}`,
        details: [
          { label: 'Aircraft', value: aircraftRegistration },
          { label: 'Priority', value: priority },
          { label: 'Reported by', value: reporterName },
        ],
        bodyText: description,
        ctaLabel: 'Review Squawk',
        ctaUrl: appUrl(`/maintenance/reports/${reportId}`),
        recipientContext: 'an administrator or maintainer',
      },
      relatedEntity: { type: 'maintenance_report', id: reportId },
      metadata: { aircraftRegistration, priority },
    });
  }

  /** OMN-19: Notify admins + maintenance when an aircraft is grounded */
  static async notifyAircraftGrounded(
    orgId: string,
    aircraftId: string,
    aircraftRegistration: string,
    reason: string,
    groundedByName: string
  ): Promise<void> {
    await this.send({
      type: 'aircraft_grounded',
      orgId,
      recipientIds: ['*admin', '*maintenance'],
      subject: `Aircraft Grounded: ${aircraftRegistration}`,
      body: `${aircraftRegistration} has been grounded.\n\nReason: ${reason}\nGrounded by: ${groundedByName}\n\nPlease review the aircraft record for details.`,
      emailContent: {
        type: 'aircraft_grounded',
        badgeLabel: 'Aircraft Grounded',
        headline: `${aircraftRegistration} has been grounded`,
        details: [
          { label: 'Aircraft', value: aircraftRegistration },
          { label: 'Reason', value: reason },
          { label: 'Grounded by', value: groundedByName },
        ],
        bodyText: 'This aircraft has been removed from the scheduling calendar. Please review the aircraft record for details.',
        ctaLabel: 'View Aircraft',
        ctaUrl: appUrl(`/aircrafts/${aircraftId}`),
        recipientContext: 'an administrator or maintainer',
      },
      relatedEntity: { type: 'aircraft', id: aircraftId },
      metadata: { aircraftRegistration, reason },
    });
  }

  /** OMN-20: Notify admins + affected member when a booking is cancelled.
   *  The 4th arg is the aircraft id; the registration is resolved for a
   *  human-readable notification (OMN-286), falling back to the id. */
  static async notifyBookingCancelled(
    orgId: string,
    bookingId: string,
    pilotId: string,
    aircraftId: string,
    bookingStart: string,
    cancelledByName: string,
    reason: string
  ): Promise<void> {
    let aircraftRegistration = aircraftId;
    try {
      const { data: ac } = await supabase
        .from('aircraft')
        .select('registration')
        .eq('id', aircraftId)
        .maybeSingle();
      if (ac?.registration) aircraftRegistration = ac.registration;
    } catch { /* fall back to the id */ }

    await this.send({
      type: 'booking_cancelled',
      orgId,
      recipientIds: ['*admin', pilotId],
      subject: `Booking Cancelled: ${aircraftRegistration} on ${new Date(bookingStart).toLocaleDateString()}`,
      body: `A booking has been cancelled.\n\nAircraft: ${aircraftRegistration}\nScheduled: ${new Date(bookingStart).toLocaleString()}\nCancelled by: ${cancelledByName}\nReason: ${reason || 'No reason provided'}`,
      emailContent: {
        type: 'booking_cancelled',
        badgeLabel: 'Booking Cancelled',
        headline: `Booking cancelled — ${aircraftRegistration} on ${new Date(bookingStart).toLocaleDateString()}`,
        details: [
          { label: 'Aircraft', value: aircraftRegistration },
          { label: 'Scheduled', value: new Date(bookingStart).toLocaleString() },
          { label: 'Cancelled by', value: cancelledByName },
          { label: 'Reason', value: reason || 'No reason provided' },
        ],
        ctaLabel: 'View Bookings',
        ctaUrl: appUrl('/bookings'),
        recipientContext: 'an administrator or the affected pilot',
      },
      relatedEntity: { type: 'booking', id: bookingId },
      metadata: { aircraftRegistration, bookingStart, reason },
    });
  }

  /**
   * A single summary notification for a recurring-series cancellation (OMN-287),
   * instead of one email per cancelled occurrence.
   */
  static async notifyBookingSeriesCancelled(
    orgId: string,
    params: {
      seriesId: string;
      pilotId: string;
      aircraftId: string;
      count: number;
      firstStart: string;
      lastStart: string;
      cancelledByName: string;
      reason: string;
    }
  ): Promise<void> {
    const { seriesId, pilotId, aircraftId, count, firstStart, lastStart, cancelledByName, reason } = params;
    let aircraftRegistration = aircraftId;
    try {
      const { data: ac } = await supabase.from('aircraft').select('registration').eq('id', aircraftId).maybeSingle();
      if (ac?.registration) aircraftRegistration = ac.registration;
    } catch {
      /* fall back to the id */
    }

    const range =
      count > 1
        ? `${new Date(firstStart).toLocaleDateString()} – ${new Date(lastStart).toLocaleDateString()}`
        : new Date(firstStart).toLocaleString();

    await this.send({
      type: 'booking_series_cancelled',
      orgId,
      recipientIds: ['*admin', pilotId],
      subject: `Recurring bookings cancelled: ${count} × ${aircraftRegistration}`,
      body: `${count} recurring booking${count === 1 ? '' : 's'} cancelled.\n\nAircraft: ${aircraftRegistration}\nDates: ${range}\nCancelled by: ${cancelledByName}\nReason: ${reason || 'No reason provided'}`,
      emailContent: {
        type: 'booking_series_cancelled',
        badgeLabel: 'Recurring Bookings Cancelled',
        headline: `${count} recurring booking${count === 1 ? '' : 's'} cancelled — ${aircraftRegistration}`,
        details: [
          { label: 'Aircraft', value: aircraftRegistration },
          { label: 'Occurrences', value: String(count) },
          { label: 'Dates', value: range },
          { label: 'Cancelled by', value: cancelledByName },
          { label: 'Reason', value: reason || 'No reason provided' },
        ],
        ctaLabel: 'View Bookings',
        ctaUrl: appUrl('/bookings'),
        recipientContext: 'an administrator or the affected pilot',
      },
      relatedEntity: { type: 'booking', id: seriesId },
      metadata: { seriesId, aircraftRegistration, count, firstStart, lastStart, reason },
    });
  }

  /** Notify a member (email + personal in-app) when their role is changed. */
  static async notifyMemberRoleChanged(
    orgId: string,
    memberId: string,
    memberName: string,
    previousRole: string,
    newRole: string,
    changedByName: string
  ): Promise<void> {
    const who = memberName?.trim() || 'You';
    await this.send({
      type: 'member_role_changed',
      orgId,
      recipientIds: [memberId],
      subject: `Your role has been updated to ${newRole}`,
      body: `${who}, your role in the organization has been changed from ${previousRole} to ${newRole} by ${changedByName}.`,
      emailContent: {
        type: 'member_role_changed',
        badgeLabel: 'Role Updated',
        headline: `Your role is now ${newRole}`,
        details: [
          { label: 'Previous role', value: previousRole },
          { label: 'New role', value: newRole },
          { label: 'Updated by', value: changedByName },
        ],
        bodyText: 'Your access in OmniFlyer reflects this change immediately. If this looks wrong, contact an administrator.',
        ctaLabel: 'Open OmniFlyer',
        ctaUrl: appUrl('/dashboard'),
        recipientContext: 'a member of this organization',
      },
      relatedEntity: { type: 'member', id: memberId },
      metadata: { previousRole, newRole },
    });
  }

  /** Notify an applicant when their club membership request is approved. */
  static async notifyMembershipRequestApproved(
    orgId: string,
    userId: string
  ): Promise<void> {
    await this.send({
      type: 'membership_request_approved',
      orgId,
      recipientIds: [userId],
      subject: 'Your membership request has been approved',
      body: "Your membership request has been approved. You can now log in to OmniFlyer and start booking aircraft, viewing the schedule, and everything else your club offers.",
      emailContent: {
        type: 'membership_request_approved',
        badgeLabel: 'Membership Approved',
        headline: 'Your membership request has been approved',
        bodyText: 'You can now log in and start booking aircraft, viewing the schedule, and everything else your club offers.',
        ctaLabel: 'Go to Dashboard',
        ctaUrl: appUrl('/dashboard'),
        recipientContext: 'an applicant to this club',
      },
    });
  }

  /** Notify an applicant when their club membership request is rejected. */
  static async notifyMembershipRequestRejected(
    orgId: string,
    userId: string,
    reason: string
  ): Promise<void> {
    await this.send({
      type: 'membership_request_rejected',
      orgId,
      recipientIds: [userId],
      subject: 'Update on your membership request',
      body: `Your membership request was not approved.${reason ? `\n\nReason: ${reason}` : ''}\n\nIf you believe this is a mistake, please reach out to the club directly.`,
      emailContent: {
        type: 'membership_request_rejected',
        badgeLabel: 'Membership Request Update',
        headline: 'Your membership request was not approved',
        details: reason ? [{ label: 'Reason', value: reason }] : undefined,
        bodyText: 'If you believe this is a mistake, please reach out to the club directly.',
        recipientContext: 'an applicant to this club',
      },
    });
  }
}
